import type { Objective } from '@/common/api/types/objectives';
import type { Finding } from '@/features/agentic-insights/components/data/types';
import { UNASSIGNED_OBJECTIVE } from './unassignedObjective';

export interface ObjectiveBucket {
  /** An objective id as findings cite it, or `UNASSIGNED_OBJECTIVE`. */
  key: string;
  title: string | undefined;
  findingCount: number;
  /** Findings on this objective the run filed as risks — what makes a pill worth looking at. */
  attentionCount: number;
}

const countsFor = (findings: Finding[], key: string) => {
  const matching = findings.filter((finding) =>
    key === UNASSIGNED_OBJECTIVE
      ? finding.objectiveIds.length === 0
      : finding.objectiveIds.includes(key),
  );
  return {
    findingCount: matching.length,
    attentionCount: matching.filter((finding) => finding.group === 'risk').length,
  };
};

/**
 * The objectives this run actually spoke about, plus the unassigned bucket when anything landed in
 * it — one per pill in the filter.
 *
 * Objectives the run said nothing about are left out. They were kept here while an Objectives
 * reading existed to show the whole portfolio, zeroes included; as a filter, a pill that selects
 * nothing is a control that appears to do nothing.
 *
 * A finding naming three objectives counts under all three, so the counts sum to more than the run's
 * finding total. That is the linkage the contract describes; flattening it to a primary objective
 * would invent a precedence `objectiveIds` does not carry.
 */
export const buildObjectiveBuckets = (
  findings: Finding[],
  objectives: readonly Objective[],
): ObjectiveBucket[] => {
  const known = objectives.map<ObjectiveBucket>((objective) => ({
    key: String(objective.id),
    title: objective.title,
    ...countsFor(findings, String(objective.id)),
  }));

  // An id a finding cites that the loop's objective list does not return — deleted, or outside the
  // pages loaded. Kept so the finding stays reachable under a filter, titleless.
  const knownKeys = new Set(known.map((bucket) => bucket.key));
  const orphaned = [...new Set(findings.flatMap((finding) => finding.objectiveIds))]
    .filter((id) => !knownKeys.has(id))
    .map<ObjectiveBucket>((id) => ({
      key: id,
      title: undefined,
      ...countsFor(findings, id),
    }));

  const unassigned = countsFor(findings, UNASSIGNED_OBJECTIVE);

  return [
    ...[...known, ...orphaned].filter((bucket) => bucket.findingCount > 0),
    ...(unassigned.findingCount > 0
      ? [{ key: UNASSIGNED_OBJECTIVE, title: undefined, ...unassigned }]
      : []),
  ];
};
